import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildHighlightClearMessage,
  buildHighlightMessage,
  buildPickModeMessage,
  readBlockPick,
  readHighlightResult,
  withPreviewToken,
  type BlockHighlightTarget,
  type BlockPick,
} from '@icetus/contracts';
import { Badge, Button } from '@icetus/ui';
import { createPreviewToken } from '../api';
import { previewHostOf, siteOrigin } from '../lib/site-url';

/**
 * 控制台内联预览（P1-17 的第二半）。
 *
 * 原来只有「点预览 → 新标签页」，问题不在打开方式，而在**改一次要切两个窗口**：
 * 客户改一句文案、切到标签页、看一眼、切回来、再改。一天几十次之后，
 * 他的实际做法是"全部改完再一起看"，而这恰恰是最容易改成"整体走样"的做法。
 *
 * ## 为什么 iframe 走查询串而不是 cookie
 *
 * 服务端原本只认 `icetus_preview` cookie。这在**新标签页**里是正确的，而且比
 * 查询串更好（令牌不进地址栏、站内导航自动带上）。但 iframe 是另一回事：
 * 控制台在 `console.icetus.net`，客户站在自己的域名，两者跨站，而
 * `SameSite=Lax` 的 cookie **按规范就不会在跨站子请求里发送** ——
 * 不是隐私开关，是 SameSite 的语义。Safari 更进一步，直接拦第三方 cookie。
 *
 * 所以这里走 `?__icetus_preview=<签名>`，由服务端把令牌拼进它生成的每个站内
 * 链接（否则在 iframe 里点一下导航就掉回线上版本）。**新标签页仍然走 cookie** ——
 * 这条路径没有跨站问题，没必要把令牌写进客户能看见的地址栏。
 *
 * 两套传输各自解决一个场景，不是没统一，而是统一不了。
 */

type Device = 'desktop' | 'tablet' | 'phone';

/**
 * 三档都是**真视口宽度**，iframe 按这个宽度渲染，再等比缩放到面板里。
 *
 * 这里曾经把桌面档写成 `width: 0`（＝跟随面板宽），理由是"不做缩放，
 * 否则 12px 的字看着像 6px"。那条理由只看到了字，没看到版式：面板大约 500px 宽，
 * iframe 的视口就只有 500px，站点的 900px 断点必然命中 —— 预览拿到的是**移动版**。
 * 客户在控制台里改「顶部工具条」的文案，预览里那行字根本不出现（`.th-utility__links`
 * 在 900 以下 display:none），主导航整条也被藏掉，而线上打开明明都在。
 * 他只能得出"预览坏了"或"保存没生效"，两种都是错的。
 *
 * 所以现在按真视口渲染、等比缩放：版式必须与线上一致，字小是缩放的结果，
 * 面板底部会写明缩放比例，并给出「新标签页」看原尺寸的出口。
 */
const DEVICES: Array<{ key: Device; label: string; width: number }> = [
  { key: 'desktop', label: '桌面', width: 1280 },
  { key: 'tablet', label: '平板', width: 834 },
  { key: 'phone', label: '手机', width: 390 },
];

/**
 * 令牌 30 分钟有效。提前 10 分钟换一次，因为面板常常会开着很久 ——
 * 客户在这期间点开 iframe 里的一个商品，拿到的是"预览链接已过期"的兜底页，
 * 他不会联想到"开了半小时了"，只会觉得预览坏了。
 */
const TOKEN_REFRESH_MS = 20 * 60 * 1000;

export interface PreviewPaneProps {
  tenantId: string;
  pageId: string;
  /** 已带语言前缀的站内路径 */
  path: string;
  /** 站点主域名。null = 还没绑域名 */
  host: string | null;
  /** 每次自增就重新取令牌并刷新。保存成功后由父组件推一下 */
  reloadSignal: number;
  /** 当前有未保存的改动 —— 面板据此提示"看到的是上一次保存的版本" */
  dirty: boolean;
  /**
   * 草稿最近同步成功的时刻。有值说明框里显示的是**未保存的草稿**，
   * 而不是父组件刚打开面板那会儿的已保存内容。
   */
  draftAt: Date | null;
  /**
   * 当前编辑中的区块。有值就让预览把对应的那一段用虚线框出来。
   *
   * 这个 props 是**对象**而不是一堆散参数：父组件用 `useMemo` 算出它，
   * 同样的目标必须拿到同一个引用，否则每次父组件重渲染都会重新发一遍消息，
   * 页面里的平滑滚动会一遍遍重放 —— 看起来像预览在抖。
   */
  highlight: BlockHighlightTarget | null;
  /**
   * 点选模式：预览里的点击不导航，而是上报"点在了哪一段"。
   *
   * 只在全屏编辑时打开。普通预览必须保持可点的链接 —— 客户的第一诉求往往是
   * "这样改完，整站看起来对不对"，把导航全废掉就看不到别的页面了。
   */
  picking?: boolean;
  /** 预览页报上来的某个位置。控制台据此打开那一段的编辑面板 */
  onPick?: (pick: BlockPick) => void;
  /** 全屏变体：占满内容区，不再是一条右侧栏 */
  full?: boolean;
  onClose: () => void;
  /** 新标签页打开。走 cookie 流程，由父组件复用原来的「预览」逻辑 */
  onOpenTab: () => void;
}

export function PreviewPane({
  tenantId,
  pageId,
  path,
  host,
  reloadSignal,
  dirty,
  draftAt,
  highlight,
  picking = false,
  onPick,
  full = false,
  onClose,
  onOpenTab,
}: PreviewPaneProps) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  /**
   * 上一次高亮有没有在预览页里找到落点。`null` = 还没有回执 / 刚换过目标。
   *
   * 需要它是因为"没有虚线框"有两种完全不同的原因：这一段被「显示」关掉了，
   * 或者当前版式里根本没有这一格。两种都不能让客户自己去猜 —— 前者他会去
   * 页面里找，找不到就以为预览坏了。
   */
  const [highlightFound, setHighlightFound] = useState<boolean | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [token, setToken] = useState<string | null>(null);
  /**
   * 让 iframe 重新加载同一条地址，而不是重新取令牌。
   *
   * 原来 `reloadSignal` 进的是取令牌的依赖：保存一次要打两次接口（令牌 + 草稿），
   * 而令牌本身 30 分钟有效，重新取没有任何意义。更糟的是它改变了 `src` ——
   * 地址一变，iframe 里那张页面的会话、滚动位置、正在播的视频全部重来。
   * 现在只换 `key`：地址不变、页面重载，位置由页面自己记住。
   */
  const [frameTick, setFrameTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenTick, setTokenTick] = useState(0);
  /** 上次成功载入的时刻，只用于"刚刷新过"的提示 */
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  /**
   * 预览格子实际能占多宽（面板宽度），用来算缩放比。
   *
   * 必须实测而不是按 CSS 猜：面板在 >1180px 是两栏里的 46%，以下变成 560px 的
   * 右侧抽屉，两处宽度差一倍多，写死任何一个都会在另一种窗口尺寸下错位。
   */
  const [stageWidth, setStageWidth] = useState(0);
  const stageObserver = useRef<ResizeObserver | null>(null);

  /**
   * 用回调 ref 而不是 `useEffect + ref.current`：格子在「有 src」时才挂载，
   * effect 的依赖里就得带上 src，翻来覆去地重新观察。
   */
  const attachStage = useCallback((el: HTMLDivElement | null) => {
    stageObserver.current?.disconnect();
    stageObserver.current = null;
    if (!el) {
      setStageWidth(0);
      return;
    }
    setStageWidth(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      // 0 = 抽屉收起那一刻量到的，保留上一次的值，免得闪一帧 100% 缩放
      if (width > 0) setStageWidth(width);
    });
    observer.observe(el);
    stageObserver.current = observer;
  }, []);

  useEffect(() => () => stageObserver.current?.disconnect(), []);

  const refresh = useCallback(() => setTokenTick((n) => n + 1), []);

  useEffect(() => {
    const timer = window.setInterval(refresh, TOKEN_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    if (!host || !pageId) {
      setToken(null);
      setError(
        host ? null : '这个站点还没有可访问的域名，无法预览。先到「域名绑定」页绑定一个域名。',
      );
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    createPreviewToken(tenantId, pageId)
      .then(({ token: fresh }) => {
        if (cancelled) return;
        setToken(fresh);
        setLoadedAt(new Date());
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setToken(null);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, pageId, host, tokenTick]);

  /**
   * 内容变了 → 让 iframe 重载同一条地址。
   *
   * 与取令牌分开：令牌是"这张页面能不能看"，内容是"这张页面现在是什么"。
   * 混在一个依赖里，每次改一句文案都要多一次接口往返，而且地址换了之后
   * 浏览器把它们当两个页面，滚动位置、表单里的临时输入都会丢。
   */
  useEffect(() => {
    setFrameTick((n) => n + 1);
  }, [reloadSignal]);

  /**
   * 只有令牌在**这个页面**上有效，所以 src 随 pageId / host / 令牌一起变。
   *
   * `withPreviewToken` 与渲染层用的是同一个函数：两边一旦各写一份拼接逻辑，
   * 参数名改一次就会出现"控制台发的链接服务端不认"，而两边代码看着都对。
   */
  const src = useMemo(() => {
    if (!host || !token) return null;
    /**
     * 平台子域（`{slug}.icetus.net`）走预览宿主，客户自有域名仍走原地址。
     *
     * 预览宿主是 `preview.icetus.com/{slug}/…`，与源站同一条直连链路 ——
     * 客户站默认地址在 Cloudflare 后面，从国内打开一次要几秒到几十秒，
     * 而这个面板的使用节奏是"改一句、看一眼"，一天几十次。
     *
     * 客户绑了自己的域名时不换宿主：路径前缀只能表达平台 slug，
     * 表达不了任意主机名，硬换等于凭空发明一套映射。
     */
    const preview = previewHostOf(host);
    if (preview) {
      // 前缀交给 withPreviewToken 一起处理，与渲染层用同一个函数（顺序错了会拼出脏 URL）
      return `${preview.origin}${withPreviewToken(path, token, preview.prefix)}`;
    }
    return `${siteOrigin(host)}${withPreviewToken(path, token)}`;
  }, [host, token, path]);

  const deviceWidth = DEVICES.find((d) => d.key === device)?.width ?? 1280;
  /**
   * 缩放比。面板比设备宽时保持 1:1（手机档在宽面板里就是原尺寸），
   * 窄了就等比缩小 —— 只缩显示，不改 iframe 的布局宽度，所以断点仍按设备宽算。
   */
  const scale = stageWidth > 0 && stageWidth < deviceWidth ? stageWidth / deviceWidth : 1;

  /**
   * 把"现在编辑的是哪一段"发给 iframe。
   *
   * 只能用 postMessage：两边跨站，这里读不到 iframe 里的 DOM，量不出位置。
   * `targetOrigin` 给 `'*'` 是必须的 —— 预览的域名是客户自己的，控制台这边
   * 拿不到（也不该依赖）它的确切 origin。消息里只有区块 id 与一个中文标签，
   * 没有令牌、没有正文，被别的窗口截获也没有价值。
   */
  const sendHighlight = useCallback(() => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    setHighlightFound(null);
    win.postMessage(
      highlight ? buildHighlightMessage(highlight) : buildHighlightClearMessage(),
      '*',
    );
  }, [highlight]);

  useEffect(() => {
    sendHighlight();
  }, [sendHighlight]);

  /**
   * 开关点选。
   *
   * 页面里那个开关是**内存态**，不是 URL 参数：iframe 每次重载都是一个新的
   * 文档，所以除了这里发一次，`onLoad` 里还要再发一次 —— 少那一处，
   * 客户改完一句、预览刷一下，点选就静默失效了，表现为"越用越点不动"。
   */
  const sendPicking = useCallback(() => {
    frameRef.current?.contentWindow?.postMessage(buildPickModeMessage(picking), '*');
  }, [picking]);

  useEffect(() => {
    sendPicking();
  }, [sendPicking]);

  /**
   * 点的回调存进 ref：父组件几乎都传内联箭头函数，直接进依赖数组会让
   * message 监听每次渲染都重挂一遍，中间那一帧的消息就丢了。
   */
  const pickRef = useRef(onPick);
  pickRef.current = onPick;

  /**
   * 预览页的回执。只认自己那一种消息，别的（浏览器插件、嵌入的客服脚本）
   * 一律不动 UI —— 否则一个不相干的 postMessage 就能把提示改成"找不到"。
   */
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const found = readHighlightResult(event.data);
      if (found !== null) setHighlightFound(found);
      const pick = readBlockPick(event.data);
      if (pick) pickRef.current?.(pick);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <aside
      className={`previewpane${full ? ' previewpane--full' : ''}${
        picking ? ' previewpane--picking' : ''
      }`}
      aria-label="站点预览"
    >
      <div className="previewpane__head">
        <div className="previewpane__title">
          <strong>{full ? '全屏编辑' : '预览'}</strong>
          {dirty ? <Badge tone="warn">{draftAt ? '草稿' : '未保存'}</Badge> : null}
        </div>
        <div className="previewpane__actions">
          <div className="segmented" role="group" aria-label="预览宽度">
            {DEVICES.map((d) => (
              <button
                key={d.key}
                type="button"
                className={`segmented__item${device === d.key ? ' is-active' : ''}`}
                onClick={() => setDevice(d.key)}
                aria-pressed={device === d.key}
              >
                {d.label}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={refresh} disabled={loading || !host}>
            刷新
          </Button>
          <Button variant="ghost" size="sm" onClick={onOpenTab} disabled={!host}>
            新标签页
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {full ? '退出全屏' : '关闭'}
          </Button>
        </div>
      </div>

      <div className="previewpane__body">
        {error ? (
          <div className="previewpane__state previewpane__state--error">
            <p>{error}</p>
            {host ? (
              <Button variant="ghost" size="sm" onClick={refresh}>
                重试
              </Button>
            ) : null}
          </div>
        ) : loading && !src ? (
          <div className="previewpane__state">
            <p>正在准备预览…</p>
          </div>
        ) : src ? (
          <div
            className="previewpane__stage"
            ref={attachStage}
            /* 缩小时必须靠左：flex 居中会把溢出的左半边裁到画布外，滚都滚不回来 */
            style={{ justifyContent: scale < 1 ? 'flex-start' : 'center' }}
          >
            <iframe
              key={`${src}#${frameTick}`}
              ref={frameRef}
              /*
               * 每次刷新（`key` 变）都是一个新的 iframe，旧的高亮与点选开关
               * 自然没了。载入完成后补发一次：客户改一句、预览刷一下，
               * 虚线圈还在原处，点选也仍然可用。
               */
              onLoad={() => {
                sendHighlight();
                sendPicking();
              }}
              className="previewpane__frame"
              style={{
                inlineSize: `${deviceWidth}px`,
                /* 视觉高度要等于格子高度 → 布局高度先反过来放大，缩放之后正好填满 */
                blockSize: `${(100 / scale).toFixed(3)}%`,
                ...(scale < 1
                  ? { transform: `scale(${scale})`, transformOrigin: 'top left' }
                  : null),
              }}
              src={src}
              title="站点预览"
              /*
               * sandbox 只留"能跑脚本、能提交表单、保留自身源"。
               *
               * 片段里跑的是客户站点的代码，它和我们不同源，正常渲染需要 allow-scripts；
               * allow-same-origin 让站点的 localStorage / cookie 照常工作（否则图片懒加载、
               * 主题切换这类依赖存储的东西会静默失效）。
               * **不给 allow-top-navigation**：那是 frame-busting 的口子 ——
               * 被嵌入的页面可以把整个控制台导航走，客户会以为控制台崩了。
               */
              sandbox="allow-scripts allow-same-origin allow-forms"
              /*
               * 令牌在这条 URL 上。iframe 里一旦有外链被点击或预取，
               * 默认 Referer 会把带令牌的地址发出去。服务端也加了响应头，
               * 这里是第二道：值不需要也不该离开这个框。
               */
              referrerPolicy="no-referrer"
              loading="eager"
            />
          </div>
        ) : null}
      </div>

      <p className="previewpane__foot">
        {full ? '点页面里的任意一段，右边就会打开这一段能改的东西。' : null}
        {draftAt
          ? `显示的是未保存的草稿 · ${draftAt.toLocaleTimeString('zh-CN', { hour12: false })} 同步。访客看到的仍是已保存的版本。`
          : loadedAt
            ? `显示的是已保存的内容 · ${loadedAt.toLocaleTimeString('zh-CN', { hour12: false })} 载入。改动会同步到这里。`
            : '显示的是已保存的内容。改动会同步到这里。'}
        {scale < 1
          ? `（按 ${Math.round(scale * 100)}% 缩放显示，这一档真实宽度 ${deviceWidth}px。字小是缩放造成的，点「新标签页」看原始大小。）`
          : null}
      </p>
      {highlight && highlightFound === false ? (
        /*
         * 找不到落点时必须说话。两种原因客户分不出来：这一段被「显示」关掉了，
         * 或者它没通过渲染（组件降级 / 当前版式跳过）。
         * 不说的话，客户会盯着预览反复找那一段，最后认为预览坏了。
         *
         * 先给**能动手的那一条**：勾选框就在左边列表里，一眼能查；"组件降级"
         * 听起来像让他去排查，放在后面说。
         */
        <p className="previewpane__foot previewpane__foot--warn">
          {full ? (
            <>
              这一页上看不到「{highlight.label}」：多半是这一段被取消了「显示」，或者当前主题
              跳过了它（组件降级）。保存后线上也一样。退出全屏能在列表里核对。
            </>
          ) : (
            <>
              这一页上没有「{highlight.label}」这一段：先看左边列表里它的「显示」勾着没有 ——
              取消勾选的段，预览和线上都不会出现。勾着仍然没有，那就是当前主题跳过了这一段
              （组件降级），保存后线上也一样。
            </>
          )}
        </p>
      ) : null}
    </aside>
  );
}
